import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const API_URL = process.env.REACT_APP_API_URL || "https://trapoundapi.onrender.com";

// This cache resets every time the user refreshes the page
const analyzedCache = new Set(); 

export async function checkFirstTradeAnalysis(trade, userId, queuePopupFunc) {
  if (!userId) return;

  // CHECK 1: Local Session Cache
  // Prevents spamming reads if we just showed it 5 seconds ago
  if (analyzedCache.has(userId)) {
    console.log("🛑 [FirstTradeAnalysis] Blocked by Local Cache (Already shown this session)");
    return;
  }

  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    // CHECK 2: Database Persistence
    // Prevents showing it again if they saw it yesterday (or even years ago)
    if (userSnap.exists() && userSnap.data().hasAnalyzedFirstTrade) {
      console.log("🛑 [FirstTradeAnalysis] Blocked by Database (User analyzed in the past)");
      
      // Update local cache so we don't pay for this DB read again until next refresh
      analyzedCache.add(userId); 
      return;
    }

    // --- IF WE GET HERE, IT IS TRULY THEIR FIRST TIME ---

    // 1. Prepare Data
    const isWin = trade["Win"] === true || trade["Win"] === "TRUE";
    const followedRules = (trade["Did I follow my Rules?"] || "").toLowerCase() === "yes";
    const pnl = trade["Actual Reward"];
    const product = trade["Product"];
    
    let headline = "";
    let message = "";
    let theme = "blue"; 

    // 2. Call AI API
    try {
      const res = await fetch(`${API_URL}/analyzeFirstTrade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          isWin,
          followedRules,
          pnl,
          product,
          strategy: trade["Entry Strategy"] || "No strategy logged",
          notes: trade["Notes"] || ""
        })
      });

      if (res.ok) {
        const json = await res.json();
        if (json.headline && json.message) {
          headline = json.headline;
          message = json.message;
          const sentiment = (json.sentiment || "").toLowerCase();
          theme = sentiment === "positive" ? "green" : 
                  sentiment === "caution" ? "yellow" : 
                  sentiment === "negative" ? "red" : "blue";
        }
      }
    } catch (apiError) {
      console.warn("⚠️ [FirstTradeAnalysis] AI failed, using fallback logic.");
    }

    // 3. Fallback Logic
    if (!message) {
      if (isWin && followedRules) {
        headline = "First Trade: Textbook Start! 📘";
        theme = "green";
        message = "You won your very first trade AND followed your rules. This is the perfect foundation.";
      } else if (isWin && !followedRules) {
        headline = "First Trade: Lucky Start ⚠️";
        theme = "yellow";
        message = "You secured a profit on your first trade, but you didn't follow your rules. Beware of bad habits.";
      } else if (!isWin && followedRules) {
        headline = "First Trade: Good Loss 🛡️";
        theme = "blue";
        message = "You took a loss on your first trade, but you followed your rules. This is a victory for discipline.";
      } else {
        headline = "First Trade: Rough Start 🛑";
        theme = "red";
        message = "You took a loss and deviated from your rules on day one. Focus 100% on execution for the next one.";
      }
    }

    // 4. Queue Popup
    queuePopupFunc("firstTradeAnalysisPopup", { headline, message, theme });

    // 5. Save Flag to DB (CRITICAL)
    // We await this to ensure it completes before we update the cache
    await setDoc(userRef, { hasAnalyzedFirstTrade: true }, { merge: true });
    
    // 6. Update local cache to prevent re-runs
    analyzedCache.add(userId);

  } catch (error) {
    console.error("🚨 [FirstTradeAnalysis] Error:", error);
  }
}